import { forwardRef } from "react";
import { CORE_MONTHLY, SCREENING_LIMITS, SUN, SUN_DEEP, INK, fmt, fmt0 } from "./config";

/* Branded one-pager surfaced only during window.print() (data-print-quote). */
export const PrintQuote = forwardRef(function PrintQuote(props, ref) {
  const {
    selectedModules, perCompanyStack, companies, tier, isCustom, discount,
    effectivePerCo, perCoSubtotal, hvSurcharge, highVolume,
    teamLines, teamSubtotal, monthlyPlatform, annualPlatform,
    seats, trainingAnnual, trPerSeat,
  } = props;
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  return (
    <div ref={ref} data-print-quote-card data-testid="print-quote-card" style={{ fontFamily: "Georgia, serif", color: INK }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: `2px solid ${SUN}`, paddingBottom: 20, marginBottom: 24 }}>
        <div>
          <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.14em", color: SUN_DEEP, marginBottom: 4 }}>
            GUARDIAN PORTFOLIO — INDICATIVE QUOTE
          </div>
          <h1 style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>CompliSun · Program estimate</h1>
          <p style={{ fontSize: 12, color: "#555", marginTop: 6 }}>Prepared {today} · Detroit-rooted · Attorney-led</p>
        </div>
        <div style={{ textAlign: "right" }}>
          <img src="/brand/complisun-mark.png" alt="CompliSun" style={{ height: 48, width: 48, objectFit: "contain" }} />
        </div>
      </div>

      {/* Per-company section */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "0.14em", color: SUN_DEEP, margin: "0 0 8px" }}>Per-company program</h2>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <tbody>
            <tr><td>Guardian Portfolio (Core, always included)</td><td style={{ textAlign: "right" }}>{fmt(CORE_MONTHLY)} / co / mo</td></tr>
            {selectedModules.map((m) => (
              <tr key={m.key}><td>{m.label}</td><td style={{ textAlign: "right" }}>{fmt(m.price)} / co / mo</td></tr>
            ))}
            <tr style={{ borderTop: "1px solid #ccc", fontWeight: 600 }}>
              <td style={{ paddingTop: 6 }}>Per-company stack</td>
              <td style={{ textAlign: "right", paddingTop: 6 }}>{fmt(perCompanyStack)} / co / mo</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Companies + discount */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "0.14em", color: SUN_DEEP, margin: "0 0 8px" }}>Portfolio size &amp; volume tier</h2>
        {isCustom ? (
          <p style={{ fontSize: 13 }}>51+ companies — contact us for portfolio pricing.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              <tr><td>Companies</td><td style={{ textAlign: "right" }}>× {companies}</td></tr>
              <tr><td>Volume tier ({tier.label})</td><td style={{ textAlign: "right" }}>{Math.round(discount * 100)}% off</td></tr>
              <tr><td>Effective per-company rate</td><td style={{ textAlign: "right" }}>{fmt(effectivePerCo)} / co / mo</td></tr>
              <tr style={{ borderTop: "1px solid #ccc", fontWeight: 600 }}>
                <td style={{ paddingTop: 6 }}>Per-company subtotal</td>
                <td style={{ textAlign: "right", paddingTop: 6 }}>{fmt(perCoSubtotal)} / mo</td>
              </tr>
              {hvSurcharge > 0 && (
                <tr><td>High-volume onboarding surcharge</td><td style={{ textAlign: "right" }}>+ {fmt(hvSurcharge)} / mo</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Team */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "0.14em", color: SUN_DEEP, margin: "0 0 8px" }}>Shared fractional team (portfolio-level)</h2>
        {teamLines.length === 0 ? (
          <p style={{ fontSize: 12, color: "#666" }}>No team roles selected.</p>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <tbody>
              {teamLines.map((l) => (
                <tr key={l.key}>
                  <td>{l.label} — {l.mode === "committed" ? `${l.hours} hrs/wk committed @ ${fmt0(l.committed)}/hr` : `ad-hoc @ ${fmt0(l.adhoc)}/hr`}</td>
                  <td style={{ textAlign: "right" }}>{l.mode === "committed" ? `${fmt(l.monthly)} / mo` : "as used"}</td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #ccc", fontWeight: 600 }}>
                <td style={{ paddingTop: 6 }}>Team subtotal (shared, not × companies)</td>
                <td style={{ textAlign: "right", paddingTop: 6 }}>{fmt(teamSubtotal)} / mo</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      {/* Total */}
      <div style={{ background: SUN, color: INK, padding: 16, borderRadius: 8, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <div style={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.14em" }}>MONTHLY PLATFORM TOTAL</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>per-company subtotal + team subtotal</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 32, fontWeight: 600 }}>{isCustom ? "Contact us" : fmt(monthlyPlatform)}</div>
            {!isCustom && <div style={{ fontSize: 12 }}>{fmt(annualPlatform)} / yr</div>}
          </div>
        </div>
      </div>

      {/* Separate lines */}
      <div style={{ marginBottom: 20, fontSize: 13 }}>
        <h2 style={{ fontSize: 14, textTransform: "uppercase", letterSpacing: "0.14em", color: SUN_DEEP, margin: "0 0 8px" }}>Separate lines</h2>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <tr>
              <td>Team training — {seats} seats × {fmt0(trPerSeat)}/seat/yr</td>
              <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(trainingAnnual)} / yr</td>
            </tr>
            <tr>
              <td>Legal counsel — via CompliSun Legal, PLC (separate engagement)</td>
              <td style={{ textAlign: "right", color: "#666" }}>request separately</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 10, color: "#666", marginTop: 24, borderTop: "1px solid #eee", paddingTop: 12 }}>
        Estimates only. Final scope, screening allotments, and pricing confirmed on kickoff call.
        {highVolume && " High-volume onboarding surcharge included above."}
        {" "}Screening allotments: AML {SCREENING_LIMITS.aml.included} / co / mo included ({fmt0(SCREENING_LIMITS.aml.overage)} each after);
        Vendor {SCREENING_LIMITS.vendor.included} / co / mo included ({fmt0(SCREENING_LIMITS.vendor.overage)} each after).
      </p>
      <p style={{ fontSize: 10, color: "#666", marginTop: 6 }}>
        CompliSun · complisunguardian.com · colindarke@complisun.com
      </p>
    </div>
  );
});
